import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Toolbar from '@mui/material/Toolbar';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import Container from '@mui/material/Container';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { Add } from '@mui/icons-material';
import {
  Button,
  Popover,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { getBillables } from '../../../../redux/billables/actions';

import BarnHorsesAutocomplete from '../../../../components/Autocompletes/BarnHorses';
import BarnUsersAutocomplete from '../../../../components/Autocompletes/BarnUsers';
import TaskDefinitionAutocomplete from '../../../../components/Autocompletes/TaskDefinition';
import BillableStatusSelect from '../../../../components/EnumSelects/BillableStatusSelect';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../utils/dateHelper';
import theme from '../../../../utils/theme';
import BillablesTable from '../BillablesTable';

const HorseBillableSelectPopover = ({
  anchorEl,
  open,
  onClose,
  selectedHorseBillables,
  onChangeSelectedHorseBillables,
  setOpenAddEditBillableModal,
}) => {
  const dispatch = useDispatch();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [pageSize, setPageSize] = React.useState(25);
  const [page, setPage] = React.useState(0);
  const [sortModel] = React.useState([
    {
      field: 'billableDate',
      sort: 'desc',
    },
  ]);
  const [taskType, setTaskType] = React.useState(null);
  const [selectedBarnHorses, setSelectedBarnHorses] = React.useState([]);
  const [selectedBarnUsers, setSelectedBarnUsers] = React.useState([]);
  const [status, setStatus] = React.useState('Open');
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);

  // Redux
  const billables = useSelector((state) => state.billables.billables);
  const isFetchingBillables = useSelector((state) => state.billables.isFetchingBillables);
  const allBarnHorses = useSelector((state) => state.barn.allBarnHorses);

  React.useEffect(() => {
    if (open) {
      dispatch(getBillables(selectedBarnHorses.map((bh) => bh.horse.id), selectedBarnUsers.map((bu) => bu.id), '', taskType && taskType.id, null, status || '', startDate, endDate, (page), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`)));
    }
  }, [status, selectedBarnHorses, selectedBarnUsers, startDate, endDate, taskType, pageSize, page, sortModel, open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <AppBar
        position="relative"
        color="inherit"
        sx={{ mb: 2 }}
        elevation={2}
      >
        <Container
          maxWidth="100%"
          sx={{
            pt: 1,
            pb: 0.5,
            overflow: 'hidden',
            maxHeight: 800,
          }}
        >
          <Toolbar disableGutters variant="dense">
            <Grid container spacing={2} justifyContent="space-between">
              <Grid>
                <Grid container spacing={2} alignItems="center">
                  <Grid sx={{ minWidth: 175 }}>
                    <BillableStatusSelect
                      includeAllOption
                      includeExpenseOption={false}
                      value={status || ''}
                      size="small"
                      onChange={(e) => setStatus(e.target.value)}
                    />
                  </Grid>
                  <Grid sx={{ minWidth: 200 }}>
                    <TaskDefinitionAutocomplete
                      label="Transaction Category"
                      value={taskType || null}
                      size="small"
                      onSelect={(e, taskDefinition) => setTaskType(taskDefinition)}
                      eventTemplates={null}
                      includeHorseTaskTypeTagId
                      includeEventTaskTypeTagId={false}
                      includeBarnTaskTypeTagId={false}
                      multiple={false}
                    />
                  </Grid>
                  <Grid sx={{ minWidth: 200 }}>
                    <BarnHorsesAutocomplete
                      multiple
                      value={selectedBarnHorses}
                      onSelect={(e, newHorses) => { setSelectedBarnHorses(newHorses) }}
                      limitTags={1}
                      size="small"
                      barnHorses={allBarnHorses}
                      label="Horse(s)"
                    />
                  </Grid>
                  <Grid sx={{ minWidth: 200 }}>
                    <BarnUsersAutocomplete
                      multiple
                      value={selectedBarnUsers}
                      onSelect={(e, newUsers) => { setSelectedBarnUsers(newUsers) }}
                      limitTags={1}
                      size="small"
                      label="User(s)"
                    />
                  </Grid>

                  <Grid>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="Start Date"
                        value={toMomentValue(startDate)}
                        disabled={isFetchingBillables}
                        onChange={(newValue) => {
                          setStartDate(fromPickerValue(newValue));
                        }}
                        slotProps={{
                          textField: {
                            sx: { width: 150 },
                            size: 'small',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="End Date"
                        value={toMomentValue(endDate)}
                        disabled={isFetchingBillables}
                        onChange={(newValue) => {
                          setEndDate(fromPickerValue(newValue));
                        }}
                        slotProps={{
                          textField: {
                            sx: { width: 150 },
                            size: 'small',
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid>
                    {
                      isFetchingBillables
                        ? (
                          <CircularProgress size={35} />
                        )
                        : (
                          <Box />
                        )
                    }
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Toolbar>
        </Container>
      </AppBar>
      <BillablesTable
        fullscreen={fullScreen}
        isFetchingBillables={isFetchingBillables}
        billables={billables}
        page={page}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setPage={setPage}
        setSelectedBillable={null}
        setOpenAddEditBillableModal={null}
        setOpenStatusChangeMenu={null}
        setStatusChangeMenuAnchorEl={null}
        selectMode
        selectedHorseBillables={selectedHorseBillables}
        onChangeSelectedHorseBillables={onChangeSelectedHorseBillables}
      />
      <Box
        sx={{
          p: 2,
          justifyContent: 'flex-end',
          display: 'flex',
        }}
      >
        <Button size="small" startIcon={<Add />} variant="outlined" sx={{ mr: 2 }} onClick={setOpenAddEditBillableModal}>New Transaction</Button>
        <Button size="small" variant="contained" onClick={onClose}>Done</Button>
      </Box>
    </Popover>
  );
}

HorseBillableSelectPopover.propTypes = {
  open: PropTypes.bool,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  onChangeSelectedHorseBillables: PropTypes.func,
  selectedHorseBillables: PropTypes.array,
  setOpenAddEditBillableModal: PropTypes.func.isRequired,
};

HorseBillableSelectPopover.defaultProps = {
  open: false,
  anchorEl: null,
  onClose: () => { },
  selectedHorseBillables: [],
  onChangeSelectedHorseBillables: () => { },
};

export default (HorseBillableSelectPopover);
